import {
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore/lite";
import { doc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  projectsSchemeDB,
  updateRecord,
  updateFieldsInRecord,
} from "../../libs/firebase";
import { firebaseHelper, generateID, getActiveDesignFirmId } from "ui";
import { ProjectSchemeInterface, Permissions } from "ts-interfaces";
import { hasAccessToResource } from "ui/utils/usePermissions";
import { teamScopeConstraintsLite, type TeamScope } from "./accessQueries";
import { freshAccessFields, stripAccessFields } from "./accessFields";
import { teamAnalytics, teammateItem } from "../../team/teamAnalytics";

// The workspace's projects: in a team, the ones the user can read (by the
// (design_firm_id, readers, updatedAt) index); in Personal, the user's own.
// The middle two are unused since every team runs the sharing model and
// Personal is owner-only (kept for callers until PR6).
export async function getProjectsScheme(
  uid: string,
  _sharedProjectIds: string[] = [],
  _designFirmId: string | null = null,
  teamScope: TeamScope | null = null,
): Promise<ProjectSchemeInterface[]> {
  if (!projectsSchemeDB) return [];
  if (!uid) throw new Error("User ID not provided");

  try {
    const snapshot = await getDocs(
      query(
        projectsSchemeDB,
        ...(teamScope ? teamScopeConstraintsLite(teamScope) : [where("owner", "==", uid)]),
        orderBy("updatedAt", "desc"),
      ),
    );
    return snapshot.docs.map((doc) => doc.data() as ProjectSchemeInterface);
  } catch (error) {
    console.error("Error fetching project schemas:", error);
    return [];
  }
}
/**
 * Create a new project.
 */
export async function createProjectScheme(
  project: Omit<ProjectSchemeInterface, "id" | "createdAt" | "updatedAt">,
): Promise<ProjectSchemeInterface> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  const newProject = {
    ...project,
    id: generateID(),
    owner: user.uid,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    // Stamp the active workspace at create time (Phase A.3.3).
    design_firm_id: getActiveDesignFirmId(user.uid),
    // Teams sharing: a new project starts private to its creator.
    ...freshAccessFields(user.uid),
  };

  await updateRecord(newProject.id, "projectsScheme", newProject);
  return newProject;
}

// Helper function to check if user has access to a project
export const hasProjectAccess = (
  projectId: string,
  userId: string,
  permissions: Permissions | null,
  project?: ProjectSchemeInterface | null,
): boolean => {
  return hasAccessToResource(
    permissions,
    projectId,
    "read",
    "project",
    project?.owner || "",
    userId,
    // No firm: plain team members never passed this gate (flag off), and a
    // flagged doc answers from its readers.
    null,
    project,
  );
};

export async function getProjectSchemeById(
  projectId: string,
): Promise<ProjectSchemeInterface | null> {
  if (!projectsSchemeDB) return null;
  if (!projectId) throw new Error("Project ID not provided");

  try {
    // A document read, not a list query: the projectsScheme list rule will
    // need provable scoping, while get stays per-document.
    const snapshot = await getDoc(doc(firebaseHelper.db, "projectsScheme", projectId));
    if (!snapshot.exists()) return null;
    return snapshot.data() as ProjectSchemeInterface;
  } catch (error) {
    console.error("Error fetching project by ID:", error);
    return null;
  }
}

export async function getAssociatedProjects(
  projectId: string,
): Promise<ProjectSchemeInterface[]> {
  if (!projectsSchemeDB) return [];

  return [];
}

/**
 * Update only the named fields on a project doc (updateDoc). Use this over
 * editProjectScheme for single-field edits like the cover image — the latter
 * writes the caller's whole (possibly stale) snapshot, reverting a teammate's
 * concurrent edit on a shared project.
 */
export async function updateProjectFields(
  projectId: string,
  fields: Partial<ProjectSchemeInterface>,
): Promise<void> {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error("User not authenticated");
  await updateFieldsInRecord(projectId, "projectsScheme", fields);
}

export async function editProjectScheme(
  project: ProjectSchemeInterface,
): Promise<ProjectSchemeInterface> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  // filtering out "boards" and "collections" from the project object; the
  // access fields are backend-owned, so a merge save leaves them untouched
  const { boards, collections, ...projectToSave } = stripAccessFields(project);

  const newProject = {
    ...projectToSave,
    updatedAt: Date.now(),
  };

  try {
    await updateRecord(newProject.id, "projectsScheme", newProject); // Corrected the variable name
  } catch (error) {
    console.error("Error updating project:", error); // Log the error
    throw new Error("Failed to update project"); // Optionally rethrow the error with a custom message
  }
  teamAnalytics.itemEdited(teammateItem("project", project.id, project, "editor"));
  return {
    ...project,
    updatedAt: newProject.updatedAt,
  };
}

/**
 * Duplicate a project.
 */
export async function duplicateProjectScheme(
  project: ProjectSchemeInterface,
): Promise<ProjectSchemeInterface> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  // Create a copy of the project, but remove "collections" and "boards" fields if they exist.
  // This is done using object destructuring to omit those fields.
  // seededStarterContent goes too: the copy is the user's own project, so it
  // must not inherit the "Sample" badge or the checklist's seeded exclusion (#3939).
  const { collections, boards, seededStarterContent, ...rest } =
    stripAccessFields(project);

  const duplicatedProject = {
    ...rest, // Spread the rest of the project fields (without collections and boards)
    name: `Copy of ${project.name}`,
    id: generateID(),
    owner: user.uid, // Set the current user as the owner
    // The copy lands where the user is, not in the source's team.
    design_firm_id: getActiveDesignFirmId(user.uid),
    createdAt: Date.now(),
    updatedAt: Date.now(),
    // collections and boards are intentionally omitted; the copy starts private
    ...freshAccessFields(user.uid),
  };

  try {
    await updateRecord(
      duplicatedProject.id,
      "projectsScheme",
      duplicatedProject,
    );
  } catch (error) {
    console.error("Error updating project:", error); // Log the error
    throw new Error("Failed to update project"); // Optionally rethrow the error with a custom message
  }
  return duplicatedProject;
}

/**
 * Delete a project.
 */
export async function deleteProjectScheme(
  project: ProjectSchemeInterface,
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  // Check if the user is the owner or an editor
  const isOwner = project.owner === user.uid;
  if (!isOwner) {
    throw new Error("You do not have permission to delete this project");
  }

  const updatedData = {
    archive: true,
    updatedAt: Date.now(),
  };

  try {
    await updateRecord(project.id, "projectsScheme", updatedData);
  } catch (error) {
    console.error("Error updating project:", error); // Log the error
    throw new Error("Failed to update project"); // Optionally rethrow the error with a custom message
  }
}

// export async function getProjectSchemeThumbnails(projectId: string): Promise<{ id: string; screenshot: string; updatedAt: number; }[]> {
//   const auth = getAuth();
//   const user = auth.currentUser;
//   if (!user) throw new Error("User not authenticated");

//   if (!projectsDB) return [];

//   const q = query(
//     projectsDB,
//     where('associatedProjectId', '==', projectId),
//     orderBy('updatedAt', 'desc'),
//     limit(3),
//   );
//   const snapshot = await getDocs(q);
//   const projectThumbnailArray = snapshot.docs.map(doc => {
//     const data:any = doc.data();
//       return {
//         id: doc.id,
//         screenshot: data.files?.screenshot,
//         updatedAt: data.updatedAt,
//       };
//   });
//   return projectThumbnailArray;
// }

// export async function getThumbnailsForProjectsScheme(projectIds: string[]): Promise<Record<string, string[]>> {
//   const auth = getAuth();
//   const user = auth.currentUser;
//   if (!user) throw new Error("User not authenticated");

//   if (!projectsDB) return {};

//   const projectThumbnailArray: Record<string, string[]> = {};

//   for (const projectId of projectIds) {
//     const thumbnails = await getProjectSchemeThumbnails(projectId);
//     projectThumbnailArray[projectId] = thumbnails.map(thumb => thumb.screenshot).filter(Boolean);
//   }

//   return projectThumbnailArray;
// }
